import { computed } from 'vue';

/**
 * Whether the tenant displays stop causes and waterfalls in the AFNOR
 * NF E60-182 order (feature flag features.displayedAsAfnor, KP-16499).
 * Plain function (not composable state): the flag is injected once per page
 * load by IndexController / widget.blade.php and read at call time.
 */
export function isDisplayedAsAfnorEnabled() {
  return !!(window?.tenant?.displayedAsAfnor?.active);
}

/**
 * Whether a tenant feature flag is on, by the key IndexController injects.
 * Plain function: the flags are injected once per page load and read at call time.
 */
export function isTenantFeatureActive(name) {
  return !!window?.tenant?.[name]?.active;
}

/**
 * Whether a user of this tenant may be pointed at the directory.
 * Plain function, like isDisplayedAsAfnorEnabled(): the value is injected once per page
 * load by IndexController / widget.blade.php and read at call time.
 */
export function isSsoAvailable() {
  return !!window?.tenant?.sso_available;
}

/**
 * Whether this tenant still admits accounts its directory does not know.
 *
 * Vrai par défaut : une valeur absente ne doit pas refermer le tenant, pas plus qu'une
 * migration ne le referme. Lue à l'appel, comme sa voisine.
 */
export function acceptsExternalAccounts() {
  return window?.tenant?.sso_accepts_external_accounts !== false;
}

/**
 * Whether the tenant adopted the new schedules and calendars (feature flag
 * features.useAvailabilityPeriods, E-0107). Plain function (not composable state), on the
 * model of isDisplayedAsAfnorEnabled: callers outside a setup() scope need it too, and
 * useSchedule() cannot serve them since it relies on inject().
 */
export function isAvailabilityPeriodsEnabled() {
  return !!(window?.tenant?.useAvailabilityPeriods?.active);
}

/**
 * Tenant-related flags exposed as composable state.
 */
export default function useTenant() {
  /**
   * Tells if we can create an event with no device.
   */
  const enableCreateDevicelessEvent = computed(() => {
    if (window.tenant && window.tenant.enable_create_deviceless_event) {
      return parseInt(window.tenant.enable_create_deviceless_event, 10) === 1;
    }

    return false;
  });

  /**
   * The date of tenant creation.
   */
  const tenantCreation = computed(() => window.tenant.tenant_creation);

  /**
   * Whether a user of this tenant may be pointed at the directory.
   */
  const ssoAvailable = computed(() => isSsoAvailable());

  /**
   * Whether this tenant still admits accounts its directory does not know.
   *
   * Enveloppée comme sa voisine, et non rendue telle quelle : ce que `setup()` rend est
   * déballé pour les refs, jamais pour les fonctions. Une fonction posée ici arriverait
   * sur l'instance en fonction — donc toujours vraie — et chaque condition qui la lit
   * serait vraie avec elle, sans que rien ne le dise.
   */
  const externalAccountsAccepted = computed(() => acceptsExternalAccounts());

  return {
    enableCreateDevicelessEvent,
    tenantCreation,
    ssoAvailable,
    acceptsExternalAccounts: externalAccountsAccepted,
  };
}
